import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Upload, Loader2, Check, AlertTriangle, ArrowRight,
  FileText, X, ChevronDown, ChevronUp, ClipboardCheck, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { statementDateToYmd } from '@/lib/gt-repayments';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SbtImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VehicleResult {
  action: 'insert' | 'update';
  stockId: string;
  vin: string;
  cc: number;
  make: string;
  model: string;
  regYear: string;
  cif: number | null;
  estArrival: string | null;
  shipDate: string | null;
  shipName: string | null;
  voyageNo: string | null;
  blTrackingNo: string | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  manufacturedAt: string | null;
  mileage: number | null;
  color: string | null;
  colorName: string | null;
  grade: string | null;
  listingUrl: string;
  scrapeFailed: boolean;
  dbId: string | null;
  currentCif: number | null;
  currentEstArrival: string | null;
  currentShipName: string | null;
  currentVoyageNo: string | null;
  currentShipDate: string | null;
  currentBlTrackingNo: string | null;
  currentInvoiceNo: string | null;
  currentInvoiceDate: string | null;
}

interface ProcessResult {
  vehicles: VehicleResult[];
  summary: { newCount: number; updateCount: number; scrapeFailures: number };
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'upload', label: 'Upload PDF', icon: Upload, description: 'Select an SBT Japan PDF' },
  { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Verify extracted vehicles' },
  { key: 'confirm', label: 'Confirm', icon: Check, description: 'Import into database' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCif(amount: number | null): string {
  if (amount == null) return '—';
  return `$${amount.toLocaleString()}`;
}

/**
 * Estimated arrival, ship date and invoice date, as the preview table and the change diff print them.
 *
 * These are calendar days, not instants: the scraped values are written straight into the
 * `vehicle_procurement` DATE columns below (`est_arrival_at`, `ship_date`, `invoice_date`), and the
 * `current*` values they are compared against come back out of those same columns as `yyyy-MM-dd`.
 * `new Date('2026-08-10')` is UTC midnight by spec, so rendering it with the browser's zone printed
 * the previous day at any negative offset — the row would show one date while writing another.
 *
 * `statementDateToYmd` is the house parser for a day written in Ghana: it takes the string apart
 * rather than handing it to `new Date`, and it REFUSES ambiguous all-numeric shapes instead of
 * guessing between dd/mm and mm/dd. SBT's markup drifts, so anything it will not vouch for falls
 * back to the previous rendering rather than disappearing behind a dash.
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';

  const ymd = statementDateToYmd(dateStr);
  if (ymd) {
    return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
  }

  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getMissingFields(v: VehicleResult): string[] {
  const missing: string[] = [];
  if (!v.manufacturedAt) missing.push('Year');
  if (v.mileage == null) missing.push('Mileage');
  if (!v.color) missing.push('Color');
  if (v.cif == null) missing.push('CIF');
  return missing;
}

function hasChanges(v: VehicleResult): boolean {
  if (v.action !== 'update') return false;
  const cifChanged = v.cif != null && v.cif !== v.currentCif;
  const arrivalChanged = v.estArrival != null && v.estArrival !== v.currentEstArrival;
  const shipNameChanged = v.shipName != null && v.shipName !== v.currentShipName;
  const voyageChanged = v.voyageNo != null && v.voyageNo !== v.currentVoyageNo;
  const shipDateChanged = v.shipDate != null && v.shipDate !== v.currentShipDate;
  const blChanged = v.blTrackingNo != null && v.blTrackingNo !== v.currentBlTrackingNo;
  const invoiceNoChanged = v.invoiceNo != null && v.invoiceNo !== v.currentInvoiceNo;
  const invoiceDateChanged = v.invoiceDate != null && v.invoiceDate !== v.currentInvoiceDate;
  return cifChanged || arrivalChanged || shipNameChanged || voyageChanged || shipDateChanged || blChanged || invoiceNoChanged || invoiceDateChanged;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SbtImportSheet({ open, onOpenChange }: SbtImportSheetProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [result, setResult] = useState<ProcessResult | null>(null);
  const [selectedInserts, setSelectedInserts] = useState<Set<string>>(new Set());
  const [selectedUpdates, setSelectedUpdates] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [newCollapsed, setNewCollapsed] = useState(false);
  const [updateCollapsed, setUpdateCollapsed] = useState(false);

  // Reset state when sheet closes
  const handleClose = () => {
    onOpenChange(false);
    // Delay reset to avoid flash during close animation
    setTimeout(() => {
      setStep(0);
      setFiles([]);
      setResult(null);
      setProcessing(false);
      setSelectedInserts(new Set());
      setSelectedUpdates(new Set());
      setConfirming(false);
      setImportDone(false);
    }, 300);
  };

  // Step validity
  const isStepComplete = (i: number) => {
    switch (i) {
      case 0: return result != null;
      case 1: return (selectedInserts.size + selectedUpdates.size) > 0;
      case 2: return importDone;
      default: return false;
    }
  };

  // ─── File Handling ──────────────────────────────────────────────────────

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = Array.from(selected).filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
    // Reset input so same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  // ─── Process PDFs ───────────────────────────────────────────────────────

  /** Merge new vehicles into existing result, deduping by VIN (latest wins). */
  function mergeResults(existing: ProcessResult | null, incoming: ProcessResult): ProcessResult {
    if (!existing) return incoming;
    const vehicleMap = new Map<string, VehicleResult>();
    for (const v of existing.vehicles) vehicleMap.set(v.vin, v);
    for (const v of incoming.vehicles) vehicleMap.set(v.vin, v); // latest wins
    const merged = Array.from(vehicleMap.values());
    return {
      vehicles: merged,
      summary: {
        newCount: merged.filter((v) => v.action === 'insert').length,
        updateCount: merged.filter((v) => v.action === 'update').length,
        scrapeFailures: merged.filter((v) => v.scrapeFailed).length,
      },
    };
  }

  const handleProcess = async () => {
    if (files.length === 0) return;
    setProcessing(true);

    try {
      const { extractPdfText } = await import('@/lib/sbt-pdf');
      let accumulated: ProcessResult | null = result; // Start from existing if adding more

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProcessingStatus(`Extracting PDF ${i + 1}/${files.length}: ${f.name}`);

        const text = await extractPdfText(f);
        if (!text || text.trim().length < 50) {
          toast.warning(`Skipped ${f.name}`, { description: 'Could not extract text.' });
          continue;
        }

        setProcessingStatus(`Processing ${i + 1}/${files.length}: ${f.name}`);

        const { data, error: fnError } = await supabase.functions.invoke(
          'process-sbt-vehicles',
          { body: { text } }
        );

        if (fnError) {
          toast.error(`Failed: ${f.name}`, { description: fnError.message });
          continue;
        }

        if (!data || data.vehicles?.length === 0) {
          toast.warning(`No vehicles in ${f.name}`);
          continue;
        }

        accumulated = mergeResults(accumulated, data);
      }

      if (!accumulated || accumulated.vehicles.length === 0) {
        toast.warning('No vehicles found in any PDF');
        setProcessing(false);
        return;
      }

      const inserts = new Set(
        accumulated.vehicles
          .filter((v) => v.action === 'insert' && getMissingFields(v).length === 0)
          .map((v) => v.vin)
      );
      const updates = new Set(
        accumulated.vehicles.filter((v) => v.action === 'update' && hasChanges(v)).map((v) => v.vin)
      );

      setResult(accumulated);
      setSelectedInserts(inserts);
      setSelectedUpdates(updates);
      setStep(1);

      // Debug: print results table to console
      console.table(accumulated.vehicles.map((v) => ({
        action: v.action,
        stockId: v.stockId,
        vin: v.vin,
        cc: v.cc,
        cif: v.cif,
        estArrival: v.estArrival,
        ship: v.shipName,
        voyage: v.voyageNo,
        shipDate: v.shipDate,
        bl: v.blTrackingNo,
        invNo: v.invoiceNo,
        invDate: v.invoiceDate,
      })));
    } catch (err) {
      toast.error('Failed to process PDFs', {
        description: (err as Error).message,
      });
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  // ─── Confirm Import ──────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!result) return;
    setConfirming(true);

    try {
      const toInsert = result.vehicles.filter(
        (v) => v.action === 'insert' && selectedInserts.has(v.vin)
      );
      const toUpdate = result.vehicles.filter(
        (v) => v.action === 'update' && selectedUpdates.has(v.vin)
      );

      let insertCount = 0;
      let updateCount = 0;

      if (toInsert.length > 0) {
        // Insert vehicle rows (without procurement fields — sync trigger handles backward compat)
        const vehicleRows = toInsert.map((v) => ({
          make: v.make.charAt(0).toUpperCase() + v.make.slice(1).toLowerCase(),
          model: v.model.charAt(0).toUpperCase() + v.model.slice(1).toLowerCase(),
          description: v.grade || `${v.make.charAt(0).toUpperCase() + v.make.slice(1).toLowerCase()} ${v.model.charAt(0).toUpperCase() + v.model.slice(1).toLowerCase()}`,
          color: v.color || 'C0C0C0',
          vin: v.vin,
          engine_size: v.cc,
          manufactured_at: v.manufacturedAt ? parseInt(v.manufacturedAt) : 0,
          mileage: v.mileage || null,
        }));

        const { data: insertedVehicles, error } = await supabase
          .from('vehicles')
          .insert(vehicleRows)
          .select('id, vin');
        if (error) throw new Error(`Insert failed: ${error.message}`);
        insertCount = vehicleRows.length;

        // Insert procurement records for newly inserted vehicles
        if (insertedVehicles && insertedVehicles.length > 0) {
          const vinToId = new Map(insertedVehicles.map((v: any) => [v.vin, v.id]));
          const procRows = toInsert
            .filter((v) => vinToId.has(v.vin))
            .map((v) => ({
              vehicle_id: vinToId.get(v.vin)!,
              listing_url: v.listingUrl,
              ...(v.cif != null ? { cif: v.cif } : {}),
              ...(v.estArrival ? { est_arrival_at: v.estArrival } : {}),
              ...(v.shipName ? { ship_name: v.shipName } : {}),
              ...(v.voyageNo ? { voyage_no: v.voyageNo } : {}),
              ...(v.shipDate ? { ship_date: v.shipDate } : {}),
              ...(v.blTrackingNo ? { bl_tracking_no: v.blTrackingNo } : {}),
              ...(v.invoiceNo ? { invoice_no: v.invoiceNo } : {}),
              ...(v.invoiceDate ? { invoice_date: v.invoiceDate } : {}),
            }));

          const { error: procError } = await supabase.from('vehicle_procurement').insert(procRows);
          if (procError) console.error('Failed to insert procurement records:', procError);
        }
      }

      if (toUpdate.length > 0) {
        for (const v of toUpdate) {
          // Build procurement updates
          const procUpdates: Record<string, unknown> = {};
          if (v.cif != null && v.cif !== v.currentCif) procUpdates.cif = v.cif;
          if (v.estArrival && v.estArrival !== v.currentEstArrival) procUpdates.est_arrival_at = v.estArrival;
          if (v.shipName != null && v.shipName !== v.currentShipName) procUpdates.ship_name = v.shipName;
          if (v.voyageNo != null && v.voyageNo !== v.currentVoyageNo) procUpdates.voyage_no = v.voyageNo;
          if (v.shipDate != null && v.shipDate !== v.currentShipDate) procUpdates.ship_date = v.shipDate;
          if (v.blTrackingNo != null && v.blTrackingNo !== v.currentBlTrackingNo) procUpdates.bl_tracking_no = v.blTrackingNo;
          if (v.invoiceNo != null && v.invoiceNo !== v.currentInvoiceNo) procUpdates.invoice_no = v.invoiceNo;
          if (v.invoiceDate != null && v.invoiceDate !== v.currentInvoiceDate) procUpdates.invoice_date = v.invoiceDate;

          if (Object.keys(procUpdates).length === 0) continue;

          // Upsert into vehicle_procurement (sync trigger handles vehicles backward compat)
          const { error } = await supabase
            .from('vehicle_procurement')
            .upsert(
              { vehicle_id: parseInt(v.dbId!), ...procUpdates },
              { onConflict: 'vehicle_id' }
            );

          if (error) console.error(`Failed to update procurement for ${v.vin}:`, error);
          else updateCount++;
        }
      }

      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });

      const parts: string[] = [];
      if (insertCount > 0) parts.push(`${insertCount} new vehicle${insertCount > 1 ? 's' : ''} added`);
      if (updateCount > 0) parts.push(`${updateCount} vehicle${updateCount > 1 ? 's' : ''} updated`);

      toast.success('Import complete', { description: parts.join(', ') });
      setImportDone(true);
      setStep(2);
    } catch (err) {
      toast.error('Import failed', { description: (err as Error).message });
    } finally {
      setConfirming(false);
    }
  };

  // ─── Toggle helpers ──────────────────────────────────────────────────────

  const toggleInsert = (vin: string) => {
    setSelectedInserts((prev) => {
      const next = new Set(prev);
      if (next.has(vin)) next.delete(vin); else next.add(vin);
      return next;
    });
  };

  const toggleUpdate = (vin: string) => {
    setSelectedUpdates((prev) => {
      const next = new Set(prev);
      if (next.has(vin)) next.delete(vin); else next.add(vin);
      return next;
    });
  };

  const toggleAllInserts = () => {
    const insertVins = result?.vehicles.filter((v) => v.action === 'insert').map((v) => v.vin) || [];
    setSelectedInserts(selectedInserts.size === insertVins.length ? new Set() : new Set(insertVins));
  };

  const toggleAllUpdates = () => {
    const updateVins = result?.vehicles.filter((v) => v.action === 'update' && hasChanges(v)).map((v) => v.vin) || [];
    setSelectedUpdates(selectedUpdates.size === updateVins.length ? new Set() : new Set(updateVins));
  };

  // ─── Render Steps ────────────────────────────────────────────────────────

  const newVehicles = result?.vehicles.filter((v) => v.action === 'insert') || [];
  const updateVehicles = result?.vehicles.filter((v) => v.action === 'update') || [];
  const totalSelected = selectedInserts.size + selectedUpdates.size;

  const renderStepContent = () => {
    switch (step) {
      case 0: // Upload
        return (
          <div className="space-y-6">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFilesSelected}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="relative group cursor-pointer border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 hover:bg-muted/30 transition-all"
            >
              {files.length > 0 ? (
                <div className="space-y-2">
                  {files.map((f) => (
                    <div key={f.name} className="flex items-center justify-center gap-3">
                      <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium line-clamp-1 max-w-[200px]">{f.name}</span>
                      <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                        className="p-1 rounded-full hover:bg-muted transition-colors"
                      >
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground mt-2">Click to add more PDFs</p>
                </div>
              ) : (
                <>
                  <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                    <Upload className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-sm font-medium">Upload PDFs</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF up to 10MB each</p>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Accepts one or more SBT Japan Reserved / Purchased Vehicles PDFs.
              <br />
              Vehicles are matched by VIN — new VINs are added, existing ones are updated.
            </p>
          </div>
        );

      case 1: // Review
        return (
          <div className="space-y-5">
            {/* Summary badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {result?.summary.newCount || 0} new
              </Badge>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {result?.summary.updateCount || 0} existing
              </Badge>
              {(result?.summary.scrapeFailures || 0) > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {result?.summary.scrapeFailures} scrape failed
                </Badge>
              )}
            </div>

            {/* New Vehicles */}
            {newVehicles.length > 0 && (
              <div className="border rounded-xl overflow-hidden">
                <button
                  onClick={() => setNewCollapsed(!newCollapsed)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-emerald-800">
                      New Vehicles ({newVehicles.length})
                    </span>
                    <span className="text-xs text-emerald-600">
                      {selectedInserts.size} selected
                    </span>
                    {(() => {
                      const warnCount = newVehicles.filter(v => getMissingFields(v).length > 0).length;
                      return warnCount > 0 ? (
                        <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          <AlertTriangle className="h-3 w-3" />
                          {warnCount} incomplete
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {newCollapsed ? <ChevronDown className="h-4 w-4 text-emerald-600" /> : <ChevronUp className="h-4 w-4 text-emerald-600" />}
                </button>
                {!newCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-3 py-2 text-left w-8">
                            <Checkbox
                              checked={selectedInserts.size === newVehicles.length && newVehicles.length > 0}
                              onCheckedChange={toggleAllInserts}
                            />
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Stock</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">VIN</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Make</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Model</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Grade</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Year</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">CC</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Mileage</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Color</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">CIF</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Est. Arrival</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Ship</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Voyage</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Ship Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">BL No.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Inv. No.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Inv. Date</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground whitespace-nowrap">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newVehicles.map((v) => {
                          const missing = getMissingFields(v);
                          const isIncomplete = missing.length > 0;
                          return (
                          <tr key={v.vin} className={cn('border-b last:border-0 transition-colors', selectedInserts.has(v.vin) ? 'bg-white' : 'bg-muted/20 opacity-50')}>
                            <td className="px-3 py-2"><Checkbox checked={selectedInserts.has(v.vin)} onCheckedChange={() => toggleInsert(v.vin)} /></td>
                            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                {isIncomplete && (
                                  <span title={`Missing: ${missing.join(', ')}`}>
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                  </span>
                                )}
                                {v.stockId}
                              </div>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{v.vin}</td>
                            <td className="px-3 py-2 capitalize whitespace-nowrap">{v.make.toLowerCase()}</td>
                            <td className="px-3 py-2 capitalize whitespace-nowrap">{v.model.toLowerCase()}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs">{v.grade || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {v.manufacturedAt || <span className="text-amber-500">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{v.cc}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{v.mileage != null ? v.mileage.toLocaleString() : <span className="text-amber-500">—</span>}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {v.color ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="h-4 w-4 rounded-full border border-border flex-shrink-0" style={{ backgroundColor: `#${v.color}` }} />
                                  {v.colorName && <span className="text-xs text-muted-foreground">{v.colorName}</span>}
                                </div>
                              ) : <span className="text-amber-500">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{v.cif != null ? formatCif(v.cif) : <span className="text-amber-500">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{v.estArrival ? formatDate(v.estArrival) : <span className="text-amber-500">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{v.shipName || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{v.voyageNo || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{v.shipDate ? formatDate(v.shipDate) : <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{v.blTrackingNo || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{v.invoiceNo || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{v.invoiceDate ? formatDate(v.invoiceDate) : <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2 text-center whitespace-nowrap">
                              <a href={v.listingUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                                <ExternalLink className="h-3.5 w-3.5 inline" />
                              </a>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Existing Vehicles */}
            {updateVehicles.length > 0 && (
              <div className="border rounded-xl overflow-hidden">
                <button
                  onClick={() => setUpdateCollapsed(!updateCollapsed)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-blue-50/50 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-blue-800">Existing Vehicles ({updateVehicles.length})</span>
                    <span className="text-xs text-blue-600">{selectedUpdates.size} selected</span>
                  </div>
                  {updateCollapsed ? <ChevronDown className="h-4 w-4 text-blue-600" /> : <ChevronUp className="h-4 w-4 text-blue-600" />}
                </button>
                {!updateCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-3 py-2 text-left w-8">
                            <Checkbox
                              checked={selectedUpdates.size === updateVehicles.filter(hasChanges).length && updateVehicles.filter(hasChanges).length > 0}
                              onCheckedChange={toggleAllUpdates}
                            />
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Stock</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">VIN</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Make</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Model</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Grade</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Year</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">CC</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Mileage</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Color</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">CIF</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Est. Arrival</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Ship</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Voyage</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Ship Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">BL No.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Inv. No.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Inv. Date</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground whitespace-nowrap">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updateVehicles.map((v) => {
                          const changed = hasChanges(v);
                          const cifChanged = v.cif != null && v.cif !== v.currentCif;
                          const arrivalChanged = v.estArrival != null && v.estArrival !== v.currentEstArrival;
                          const shipNameChanged = v.shipName != null && v.shipName !== v.currentShipName;
                          const voyageChanged = v.voyageNo != null && v.voyageNo !== v.currentVoyageNo;
                          const shipDateChanged = v.shipDate != null && v.shipDate !== v.currentShipDate;
                          const blChanged = v.blTrackingNo != null && v.blTrackingNo !== v.currentBlTrackingNo;
                          const invoiceNoChanged = v.invoiceNo != null && v.invoiceNo !== v.currentInvoiceNo;
                          const invoiceDateChanged = v.invoiceDate != null && v.invoiceDate !== v.currentInvoiceDate;
                          return (
                            <tr key={v.vin} className={cn('border-b last:border-0 transition-colors', !changed && 'opacity-40', selectedUpdates.has(v.vin) ? 'bg-white' : 'bg-muted/20 opacity-50')}>
                              <td className="px-3 py-2"><Checkbox checked={selectedUpdates.has(v.vin)} onCheckedChange={() => toggleUpdate(v.vin)} disabled={!changed} /></td>
                              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{v.stockId}</td>
                              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{v.vin}</td>
                              <td className="px-3 py-2 capitalize whitespace-nowrap">{v.make.toLowerCase()}</td>
                              <td className="px-3 py-2 capitalize whitespace-nowrap">{v.model.toLowerCase()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-xs">{v.grade || <span className="text-muted-foreground">—</span>}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{v.manufacturedAt || <span className="text-muted-foreground">—</span>}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{v.cc}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">{v.mileage != null ? v.mileage.toLocaleString() : <span className="text-muted-foreground">—</span>}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {v.color ? (
                                  <div className="flex items-center gap-1.5">
                                    <div className="h-4 w-4 rounded-full border border-border flex-shrink-0" style={{ backgroundColor: `#${v.color}` }} />
                                    {v.colorName && <span className="text-xs text-muted-foreground">{v.colorName}</span>}
                                  </div>
                                ) : <span className="text-muted-foreground">—</span>}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {cifChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="line-through text-muted-foreground">{formatCif(v.currentCif)}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{formatCif(v.cif)}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">{formatCif(v.currentCif)}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {arrivalChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="line-through text-muted-foreground">{formatDate(v.currentEstArrival)}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{formatDate(v.estArrival)}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">{formatDate(v.currentEstArrival)}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {shipNameChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="line-through text-muted-foreground">{v.currentShipName || '—'}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{v.shipName}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">{v.currentShipName || '—'}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {voyageChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs font-mono">
                                    <span className="line-through text-muted-foreground">{v.currentVoyageNo || '—'}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{v.voyageNo}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground font-mono">{v.currentVoyageNo || '—'}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {shipDateChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="line-through text-muted-foreground">{formatDate(v.currentShipDate)}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{formatDate(v.shipDate)}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">{formatDate(v.currentShipDate)}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {blChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs font-mono">
                                    <span className="line-through text-muted-foreground">{v.currentBlTrackingNo || '—'}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{v.blTrackingNo}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground font-mono">{v.currentBlTrackingNo || '—'}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {invoiceNoChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs font-mono">
                                    <span className="line-through text-muted-foreground">{v.currentInvoiceNo || '—'}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{v.invoiceNo}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground font-mono">{v.currentInvoiceNo || '—'}</span>}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {invoiceDateChanged ? (
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="line-through text-muted-foreground">{formatDate(v.currentInvoiceDate)}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-emerald-600">{formatDate(v.invoiceDate)}</span>
                                  </div>
                                ) : <span className="text-xs text-muted-foreground">{formatDate(v.currentInvoiceDate)}</span>}
                              </td>
                              <td className="px-3 py-2 text-center whitespace-nowrap">
                                <a href={v.listingUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                                  <ExternalLink className="h-3.5 w-3.5 inline" />
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 2: // Confirm / Done
        return (
          <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold">Import Complete</h3>
            <p className="text-muted-foreground text-sm max-w-xs">
              All selected vehicles have been imported into the database. You can close this sheet.
            </p>
          </div>
        );
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[90vw] !max-w-5xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
      >
        {/* ── Left Sidebar ── */}
        <div className="w-[280px] bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden flex-shrink-0">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="mb-6 relative z-10">
            <h2 className="text-2xl font-semibold tracking-tight">Sync Reservations</h2>
            <p className="text-sm text-muted-foreground mt-1">Upload an SBT Japan reservations PDF</p>
          </div>

          {/* Step nav */}
          <div className="space-y-1 relative z-10 flex-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isComplete = isStepComplete(i);

              return (
                <button
                  key={s.key}
                  onClick={() => {
                    // Only allow navigating to completed steps or current step
                    if (i <= step || isStepComplete(i - 1)) setStep(i);
                  }}
                  disabled={i > step && !isStepComplete(i - 1)}
                  className={cn(
                    'w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left',
                    isActive ? 'bg-background shadow-sm ring-1 ring-border' : 'hover:bg-background/50',
                    i > step && !isStepComplete(i - 1) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="relative">
                    <div className={cn(
                      'h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300',
                      isActive ? 'bg-primary text-primary-foreground' :
                        isComplete ? 'bg-green-100 text-green-600/70 dark:bg-green-900/40 dark:text-green-400/70' :
                          'bg-muted text-muted-foreground',
                    )}>
                      <Icon className={cn('h-5 w-5', isComplete && !isActive && 'opacity-60')} />
                    </div>
                    {isComplete && !isActive && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                        <Check className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      'font-medium text-sm transition-colors',
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {s.label}
                    </p>
                    <p className="text-xs text-muted-foreground/70 hidden lg:block">{s.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-auto flex items-center relative z-10">
            <Button
              variant="ghost"
              size="sm"
              className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
              onClick={handleClose}
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* ── Right Content ── */}
        <div className="flex-1 flex flex-col h-full relative min-w-0">
          {/* Mobile header */}
          <div className="md:hidden p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{STEPS[step].label}</h2>
              <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-col h-full w-full">
              {/* Step title */}
              <div className="flex-none mb-4 px-6 md:px-8 pt-6 md:pt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                    <p className="text-muted-foreground">{STEPS[step].description}</p>
                  </div>
                  {isStepComplete(step) && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">
                      <Check className="h-3.5 w-3.5" />
                      Complete
                    </div>
                  )}
                </div>
              </div>

              {/* Scrollable content — native overflow for both axes */}
              <div className="flex-1 min-h-0 overflow-auto px-6 md:px-8 pb-6">
                {renderStepContent()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
            <div className="flex items-center justify-between">
              {step === 0 && (
                <>
                  <Button variant="outline" onClick={handleClose} className="rounded-full">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcess}
                    disabled={files.length === 0 || processing}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {processingStatus}
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Process {files.length} PDF{files.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </>
              )}

              {step === 1 && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStep(0);
                      setFiles([]);
                    }}
                    className="rounded-full"
                  >
                    ← Upload more
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={totalSelected === 0 || confirming}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {confirming ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Confirm ({selectedInserts.size} insert{selectedInserts.size !== 1 ? 's' : ''}, {selectedUpdates.size} update{selectedUpdates.size !== 1 ? 's' : ''})
                      </>
                    )}
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <div />
                  <Button
                    onClick={handleClose}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    Done
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
